import { provide } from '@lit/context';
import { LitElement, PropertyValueMap } from 'lit';
import { property, state } from 'lit/decorators.js';

import { global, GlobalComponentType } from '@/context';
import { DownloadStatus } from '@/types';
import { i18nInit, setBaseUrl } from '@/utils';

type Constructor<T> = { new (...args: any[]): T };

export const DownloadsMixin = <T extends Constructor<LitElement>>(
  superClass: T,
) => {
  class ListDownloadsMixinClass extends superClass {
    @property({ type: String })
    baseUrl = '';

    @property({ type: String })
    locale = 'en_US';

    @property({ type: String })
    debtor = '';

    @property({ type: String })
    country: string | undefined = undefined;

    @state()
    downloadStatus!: { [key: string]: DownloadStatus };

    @state()
    downloadsQueue: { url: string; id: string }[] = [];

    @state()
    @provide({ context: global.context })
    globalContext: GlobalComponentType = {
      layout: 'wholesale',
      locale: this.locale,
      baseUrl: this.baseUrl,
      country: this.country,
      debtor: this.debtor,
    };

    async scheduleUpdate() {
      setBaseUrl(this.baseUrl);

      window.i18next = window.i18next || i18nInit(this.locale);
      await window.i18next;

      super.scheduleUpdate();
    }

    protected willUpdate(
      _changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>,
    ): void {
      if (
        !_changedProperties.has('downloadsQueue') ||
        this.downloadsQueue.length !== 2 ||
        this.downloadsQueue.some(download => !download.url)
      ) {
        return;
      }

      this.downloadsQueue.forEach((download, index) => {
        /**
         * If you trigger a download immediately after the first one, the browser will override the first download.
         * The setTimeout is a workaround to allow the first download to be triggered before the second one.
         */
        setTimeout(
          () => {
            window.location.assign(download.url);
            this.triggerDownloadStatus(download.id);
          },
          index === 0 ? 0 : 500,
        );
      });
      this.downloadsQueue = [];
    }

    getFileType(url: string) {
      const excelExtensions = [
        '.xlsx',
        '.xls',
        '.xlsm',
        '.xlsb',
        '.xltx',
        '.xltm',
        '.xlam',
        '.csv',
      ];
      const pdfExtension = '.pdf';

      if (excelExtensions.some(ext => url.includes(ext))) {
        return 'excel';
      }

      if (url.includes(pdfExtension)) {
        return 'pdf';
      }
      return undefined;
    }

    updateDownloadStatus(type: string, status: DownloadStatus) {
      this.downloadStatus = {
        ...this.downloadStatus,
        [type]: status,
      };
    }

    triggerDownloadStatus(id: string) {
      this.updateDownloadStatus(id, DownloadStatus.SUCCESS);
      setTimeout(() => {
        this.updateDownloadStatus(id, DownloadStatus.INITIAL);
      }, 5000);
    }
  }

  return ListDownloadsMixinClass;
};
